import clsx from "clsx";
import { X } from "lucide-react";
import { InputHTMLAttributes, useCallback, useState } from "react";
import { twMerge } from "tailwind-merge";
import Button from "../button";
import { SelectGroup, SelectOption } from "../control/types";
import Dropdown, { DropdownContext } from "../dropdown";
import { ControlSize } from "../types";
import { useSelect } from "./hooks";
import SelectMenu from "./selectMenu";
import SelectValue from "./selectValue";
import { OnChangeValue, Options, OptionsOrGroups, PropsValue } from "./types";

export interface SelectProps<
  Option extends SelectOption = SelectOption,
  IsMulti extends boolean = boolean,
  Group extends SelectGroup<Option> = SelectGroup<Option>,
> extends Omit<
  InputHTMLAttributes<HTMLInputElement | HTMLTextAreaElement>,
  "value" | "defaultValue" | "onChange" | "size"
> {
  menuClassName?: string;
  options: OptionsOrGroups<Option, Group>;
  value?: PropsValue<Option>;
  defaultValue?: PropsValue<Option>;
  isMulti?: IsMulti;
  isClearable?: boolean;
  size?: ControlSize;
  className?: string;
  disabled?: boolean;
  error?: boolean;
  placeholder?: string;
  emptyOption?: Option;
  getInputRef?: (instance: any) => void;
  onChange?: (newValue: OnChangeValue<Option, IsMulti>) => void;
  onCreate?: (newValue: string) => void;
  onDelete?: (option: Option) => void;
  isOptionDeletable?: (option: Option) => boolean;
  isOptionDisabled?: (option: Option, selectValue: Options<Option>) => boolean;
}

export default function Select<
  Option extends SelectOption = SelectOption,
  IsMulti extends boolean = boolean,
  Group extends SelectGroup<Option> = SelectGroup<Option>,
>(props: Readonly<SelectProps<Option, IsMulti, Group>>) {
  const {
    menuClassName,
    value,
    defaultValue,
    options = [],
    size = "md",
    isMulti,
    isClearable,
    className,
    disabled,
    error = false,
    placeholder,
    emptyOption,
    getInputRef,
    onChange,
    isOptionDisabled,
    onCreate,
    onDelete,
    isOptionDeletable,
    readOnly,
    ...rest
  } = props;

  const {
    inputValue,
    filteredOptions,
    selectedValue,
    ref,
    setInputValue,
    onSelect,
    onClose,
    onClear,
    onToggleClick,
    availableOptions,
  } = useSelect(props);

  const disableSelect = disabled || readOnly;
  const hasSelectedValues = selectedValue.length > 0;
  const clearableValueCount = selectedValue.filter(
    (option) => option.id !== emptyOption?.id,
  ).length;
  const showClearButton =
    isClearable &&
    !disableSelect &&
    (isMulti ? clearableValueCount > 1 : clearableValueCount > 0);

  const [focused, setFocused] = useState(false);
  const isAvailableOptionDisabled = useCallback(
    (option: Option, selectValue: Options<Option>) =>
      option.id === emptyOption?.id
        ? false
        : (isOptionDisabled?.(
            option,
            selectValue.filter((value) => value.id !== emptyOption?.id),
          ) ?? false),
    [emptyOption?.id, isOptionDisabled],
  );
  const isAvailableOptionDeletable = useCallback(
    (option: Option) =>
      option.id !== emptyOption?.id && (isOptionDeletable?.(option) ?? true),
    [emptyOption?.id, isOptionDeletable],
  );

  const canCreate = () => {
    return (
      !!onCreate &&
      !!inputValue.trim().length &&
      !availableOptions
        .flatMap((o) => ("options" in o ? o.options : o))
        .some(
          (o) =>
            o.label.trim().toLowerCase() === inputValue.trim().toLowerCase(),
        )
    );
  };

  return (
    <Dropdown autoClose={!isMulti} onSelect={onSelect} onClose={onClose}>
      <DropdownContext.Consumer>
        {({ isOpen, open, activeIndex, setActiveIndex }) => (
          <Dropdown.Toggle
            noStyle
            typeable
            disabled={disableSelect}
            onClick={onToggleClick}
            className={twMerge(
              clsx(
                "w-full border rounded-sm transition-all flex gap-2 items-center z-[1]",
                {
                  "text-sm py-1 px-2": size === "sm",
                  "text-base py-1.5 px-3": size === "md",
                  "text-xl py-2 px-4": size === "lg",
                  "bg-white": !disabled,
                  "bg-gray-200": disabled,
                  "border-gunmetal-200": !isOpen,
                  "ring-[3px]": isOpen || focused,
                  "ring-primary/25 border-hunter-300":
                    (isOpen || focused) && !error,
                  "ring-red-400/25 border-red-500":
                    (isOpen || focused) && error,
                },
              ),
              className,
            )}
          >
            <div
              className={clsx("w-full min-w-0 flex-wrap", {
                grid: !isMulti,
                flex: isMulti,
              })}
            >
              <SelectValue
                isMulti={isMulti}
                hasInputValue={!!inputValue}
                selectedValues={selectedValue}
                disabled={disableSelect}
                size={size}
                removeValue={onSelect}
                defaultOptionId={emptyOption?.id}
              />
              <div
                style={{
                  gridArea: "1/1/2/3",
                  gridTemplateColumns: "0 min-content",
                }}
                className={clsx({
                  "inline-grid": !inputValue && hasSelectedValues,
                  "flex-1": isMulti && (!!inputValue || !hasSelectedValues),
                  "w-[2px] shrink-0":
                    isMulti && !inputValue && hasSelectedValues,
                })}
              >
                <input
                  {...rest}
                  ref={ref}
                  disabled={disabled}
                  readOnly={readOnly}
                  className={clsx("w-full outline-hidden bg-transparent", {
                    "min-w-[1px]": !inputValue,
                  })}
                  placeholder={placeholder ?? "Select..."}
                  type="text"
                  style={{ gridArea: "1/2" }}
                  value={inputValue}
                  onFocus={() => setFocused(true)}
                  onBlur={() => setFocused(false)}
                  onChange={(e) => {
                    open();
                    setActiveIndex(0);
                    setInputValue(e.currentTarget.value);
                  }}
                  onKeyDown={(event) => {
                    const flat = filteredOptions
                      .flatMap((opt) => ("options" in opt ? opt.options : opt))
                      .map((x) => x.id);
                    if (
                      event.code === "Enter" &&
                      activeIndex != null &&
                      flat[activeIndex]
                    ) {
                      setActiveIndex(null);
                      onSelect(flat[activeIndex]);
                    } else if (
                      event.code === "Enter" &&
                      !!inputValue.trim().length
                    ) {
                      onCreate?.(inputValue.trim());
                    }

                    if (
                      event.code === "Backspace" &&
                      isMulti &&
                      !inputValue &&
                      !!selectedValue.length
                    ) {
                      onSelect(selectedValue[selectedValue.length - 1].id);
                    }

                    if (event.code === "Space") {
                      event.stopPropagation();
                    }
                  }}
                />
              </div>
            </div>
            {onCreate && canCreate() && (
              <Button
                size="sm"
                variant="light"
                mode="flat"
                className="text-xs"
                onClick={() => onCreate(inputValue.trim())}
              >
                Create
              </Button>
            )}
            {showClearButton && (
              <X size={15} onClick={onClear} className="size-3.75! shrink-0" />
            )}
          </Dropdown.Toggle>
        )}
      </DropdownContext.Consumer>
      <Dropdown.Panel
        focusOnToggle={false}
        matchReferenceWidth
        className={menuClassName}
      >
        <SelectMenu
          options={filteredOptions}
          selectedValues={selectedValue}
          isCreateEnabled={canCreate()}
          onDelete={onDelete}
          isOptionDisabled={isAvailableOptionDisabled}
          isOptionDeletable={isAvailableOptionDeletable}
        />
      </Dropdown.Panel>
    </Dropdown>
  );
}
